import React, { useState } from 'react';
import { ShoppingCartIcon, ZapIcon, AlertCircleIcon, ExternalLinkIcon, ImageOffIcon } from 'lucide-react';
export const ProductCard = ({
  product,
  viewMode,
  onAddToCart,
  onCursorChange
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [imageError, setImageError] = useState(false);
  const handleAddToCart = () => {
    if (product.available) {
      onAddToCart(product);
    }
  };
  const handleImageError = () => {
    setImageError(true);
  };
  // Unavailable product image template
  const UnavailableImageTemplate = () => <div className="w-full h-full flex flex-col items-center justify-center bg-[#0a0e17] text-[#a0c0e0]" data-id="element-1268">
      <ImageOffIcon size={24} className="mb-2 text-[#6ff8f0]/50" data-id="element-1269" />
      <p className="text-xs font-mono text-center px-4" data-id="element-1270">Image Unavailable</p>
    </div>;
  if (viewMode === 'grid') {
    return <div className="glass border border-[#1a2a40]/50 rounded-lg overflow-hidden transition-all duration-300 animate-fade-in-up" style={{
      animationDelay: `${0.1 * (product.id % 8)}s`
    }} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} data-id="element-1271">
        <div className="relative overflow-hidden aspect-square" data-id="element-1272">
          {imageError ? <UnavailableImageTemplate data-id="element-1273" /> : <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-700" style={{
          transform: isHovered ? 'scale(1.05)' : 'scale(1)'
        }} onError={handleImageError} onMouseEnter={() => onCursorChange('image')} onMouseLeave={() => onCursorChange('default')} data-id="element-1274" />}
          {/* Product badges */}
          <div className="absolute top-0 left-0 p-3 flex flex-col gap-2" data-id="element-1275">
            {product.featured && <span className="px-2 py-1 bg-[#e64cff]/90 text-white text-xs font-mono rounded" data-id="element-1276">
                FEATURED
              </span>}
            {!product.available && <span className="px-2 py-1 bg-[#1a2a40]/90 text-[#a0c0e0] text-xs font-mono rounded" data-id="element-1277">
                SOLD OUT
              </span>}
          </div>
          {/* Hover overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#030508]/90 to-transparent opacity-0 transition-opacity duration-300 flex items-end" style={{
          opacity: isHovered ? 1 : 0
        }} data-id="element-1278">
            <div className="p-4 w-full flex flex-col gap-2" data-id="element-1279">
              <a href={product.url} target="_blank" rel="noopener noreferrer" className="w-full py-2 px-4 rounded flex items-center justify-center gap-2 text-sm font-mono transition-all duration-300 bg-[#6ff8f0]/20 text-[#6ff8f0] hover:bg-[#6ff8f0]/30 border border-[#6ff8f0]/50" onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-1280">
                <ExternalLinkIcon size={14} data-id="element-1281" />
                <span data-id="element-1282">VIEW PRODUCT</span>
              </a>
              <button className={`w-full py-2 px-4 rounded flex items-center justify-center gap-2 text-sm font-mono transition-all duration-300 ${product.available ? 'bg-[#6ff8f0]/20 text-[#6ff8f0] hover:bg-[#6ff8f0]/30 border border-[#6ff8f0]/50' : 'bg-[#1a2a40]/50 text-[#a0c0e0] cursor-not-allowed border border-[#1a2a40]'}`} onClick={handleAddToCart} disabled={!product.available} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-1283">
                {product.available ? <>
                    <ShoppingCartIcon size={14} data-id="element-1284" />
                    <span data-id="element-1285">ADD TO CART</span>
                  </> : <>
                    <AlertCircleIcon size={14} data-id="element-1286" />
                    <span data-id="element-1287">SOLD OUT</span>
                  </>}
              </button>
            </div>
          </div>
        </div>
        <div className="p-4" data-id="element-1288">
          <h3 className="text-[#e0e6f0] font-bold mb-1 font-heading" onMouseEnter={() => onCursorChange('text')} onMouseLeave={() => onCursorChange('default')} data-id="element-1289">
            {product.name}
          </h3>
          <div className="flex justify-between items-center" data-id="element-1290">
            <p className="text-[#6ff8f0] font-mono" onMouseEnter={() => onCursorChange('text')} onMouseLeave={() => onCursorChange('default')} data-id="element-1291">
              ${product.price}
            </p>
            <p className="text-[#a0c0e0] text-xs font-mono uppercase" onMouseEnter={() => onCursorChange('text')} onMouseLeave={() => onCursorChange('default')} data-id="element-1292">
              {product.category}
            </p>
          </div>
        </div>
      </div>;
  } else {
    // List view
    return <div className="glass border border-[#1a2a40]/50 rounded-lg overflow-hidden transition-all duration-300 animate-fade-in-up flex" style={{
      animationDelay: `${0.1 * (product.id % 8)}s`
    }} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} data-id="element-1293">
        <div className="relative overflow-hidden w-32 h-32 sm:w-48 sm:h-48" data-id="element-1294">
          {imageError ? <UnavailableImageTemplate data-id="element-1295" /> : <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-700" style={{
          transform: isHovered ? 'scale(1.05)' : 'scale(1)'
        }} onError={handleImageError} onMouseEnter={() => onCursorChange('image')} onMouseLeave={() => onCursorChange('default')} data-id="element-1296" />}
          {/* Product badges */}
          <div className="absolute top-0 left-0 p-2 flex flex-col gap-1" data-id="element-1297">
            {product.featured && <span className="px-2 py-1 bg-[#e64cff]/90 text-white text-xs font-mono rounded" data-id="element-1298">
                FEATURED
              </span>}
            {!product.available && <span className="px-2 py-1 bg-[#1a2a40]/90 text-[#a0c0e0] text-xs font-mono rounded" data-id="element-1299">
                SOLD OUT
              </span>}
          </div>
        </div>
        <div className="p-4 flex-1 flex flex-col justify-between" data-id="element-1300">
          <div data-id="element-1301">
            <div className="flex justify-between items-start" data-id="element-1302">
              <h3 className="text-[#e0e6f0] font-bold mb-1 font-heading" onMouseEnter={() => onCursorChange('text')} onMouseLeave={() => onCursorChange('default')} data-id="element-1303">
                {product.name}
              </h3>
              <p className="text-[#6ff8f0] font-mono" onMouseEnter={() => onCursorChange('text')} onMouseLeave={() => onCursorChange('default')} data-id="element-1304">
                ${product.price}
              </p>
            </div>
            <p className="text-[#a0c0e0] text-xs font-mono uppercase mb-2" onMouseEnter={() => onCursorChange('text')} onMouseLeave={() => onCursorChange('default')} data-id="element-1305">
              {product.category}
            </p>
            <p className="text-[#a0c0e0] text-sm hidden sm:block" onMouseEnter={() => onCursorChange('text')} onMouseLeave={() => onCursorChange('default')} data-id="element-1306">
              {product.description}
            </p>
          </div>
          <div className="mt-4 flex gap-2" data-id="element-1307">
            <a href={product.url} target="_blank" rel="noopener noreferrer" className="py-2 px-4 rounded flex items-center justify-center gap-2 text-sm font-mono transition-all duration-300 bg-[#6ff8f0]/20 text-[#6ff8f0] hover:bg-[#6ff8f0]/30 border border-[#6ff8f0]/50" onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-1308">
              <ExternalLinkIcon size={14} data-id="element-1309" />
              <span data-id="element-1310">VIEW</span>
            </a>
            <button className={`py-2 px-4 rounded flex items-center justify-center gap-2 text-sm font-mono transition-all duration-300 ${product.available ? 'bg-[#6ff8f0]/20 text-[#6ff8f0] hover:bg-[#6ff8f0]/30 border border-[#6ff8f0]/50' : 'bg-[#1a2a40]/50 text-[#a0c0e0] cursor-not-allowed border border-[#1a2a40]'}`} onClick={handleAddToCart} disabled={!product.available} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-1311">
              {product.available ? <>
                  <ShoppingCartIcon size={14} data-id="element-1312" />
                  <span data-id="element-1313">ADD TO CART</span>
                </> : <>
                  <AlertCircleIcon size={14} data-id="element-1314" />
                  <span data-id="element-1315">SOLD OUT</span>
                </>}
            </button>
          </div>
        </div>
      </div>;
  }
};